import type { StaticImageData } from 'next/image'
import { isBoolean, isNil, isNumber, isString, uniq } from 'lodash'
import pngLogo from '@/assets/images/fileLogos/text_flat_png.svg'
import pdfLogo from '@/assets/images/fileLogos/text_flat_pdf.svg'
import jpegLogo from '@/assets/images/fileLogos/text_flat_jpeg.svg'
import jpgLogo from '@/assets/images/fileLogos/text_flat_jpg.svg'
import jsonLogo from '@/assets/images/fileLogos/text_flat_json.svg'
/**
 * Transform a JSON to a CSV format
 * @param {object[]} data JSON to transform
 * @param {string[]} columns Columns the header row starts with, also when there is no row
 * @return {string}
 */
export const JSONtoCsv = (data: object[], columns: readonly string[] = []): string => {
    const rows = data.map((row) => new Map<string, unknown>(Object.entries(row)))
    const header = uniq([...columns, ...rows.flatMap((row) => [...row.keys()])])
    const csv = rows.map((row) => header.map((fieldName) => JSON.stringify(row.get(fieldName) || '')).join(','))
    csv.unshift(header.join(','))

    const finalCsv = csv.join('\r\n')
    return finalCsv
}

/**
 * Build an URL from a file object, choose if you want to download this file or open it in a new browser tab
 * @param {Blob} file File to format
 * @param {string} fileName Name of file to download
 * @param {boolean} isDownload Whether the file is downloaded or opened in a new tab
 * @return {void}
 */
export const resolveFileOnBrowser = (
    file: Blob | undefined,
    fileName: string | undefined,
    isDownload: boolean
): void => {
    if (!isNil(file)) {
        const url = window.URL.createObjectURL(file)

        if (isDownload) {
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', fileName ?? 'advance-attachment-aria')

            document.body.appendChild(link)
            link.click()
            link?.parentNode?.removeChild(link)
        } else {
            window.open(url)
        }
    }
}

interface Deprecated_TranslateJSONOptions {
    valueTranslation: Record<string, (key: string, value: string | number | boolean) => string>
    keyTranslation: (value: string) => string
}

/**
 * Translate each key of an array of object
 * @param {Record<string, any>[]} data Array of object
 * @param {Deprecated_TranslateJSONOptions} options Options to manage your translation
 * @return {Record<string, any>[]}
 */

export const deprecated_translateJSON = (
    // eslint-disable-next-line typescript/no-explicit-any -- a generic over any record; unknown would reject the interfaces callers pass
    data: Record<string, any>[],
    options: Deprecated_TranslateJSONOptions
): typeof data => {
    const { valueTranslation, keyTranslation } = options || {}

    return data.map((row) =>
        Object.entries(row).reduce(
            (acc, [key, value]) => ({
                ...acc,
                [keyTranslation(key)]:
                    (isString(value) || isNumber(value) || isBoolean(value)) && valueTranslation[key]
                        ? valueTranslation[key](key, value)
                        : value
            }),
            {}
        )
    )
}

type Primitive = string | boolean | null | undefined

interface TranslateJSONOptions<T extends Record<string, unknown>> {
    /**
     * Optional per-key value transformers. Only the keys of T are allowed.
     */
    valueTranslation?: Partial<{
        [K in keyof T]: (key: K, value: T[K]) => Primitive
    }>
    /**
     * Translator for column names. Only receives keys of T.
     */
    keyTranslation: (key: keyof T) => string
}

/**
 * Translate each key of an array of objects and optionally transform values.
 */
export function translateJSON<T extends Record<string, unknown>>(
    data: readonly T[],
    { valueTranslation, keyTranslation }: TranslateJSONOptions<T>
): Array<Record<string, unknown>> {
    return data.map((row) => {
        const out: Record<string, unknown> = {}
        ;(Object.keys(row) as Array<keyof T>).forEach((key) => {
            const translatedKey = keyTranslation(key)
            const value = row[key]
            const transform = valueTranslation?.[key]

            out[translatedKey] = transform ? transform(key, value) : value
        })
        return out
    })
}

export type FileInfo = {
    name: string
    size: {
        value: number
        unit: string
    }
    type: string
}

export const fileSizeUnits = ['b', 'kb', 'mb', 'gb']
export type FileSizeUnit = (typeof fileSizeUnits)[number]

export const allowedFileTypes: File['type'][] = ['image/jpeg', 'image/png', 'application/pdf', 'application/json']
export type AllowedFileType = (typeof allowedFileTypes)[number]

export type ParseFileInfoOptions = {
    file: File | undefined
    sizeUnit?: FileSizeUnit
}

export const parseFileInfo = ({ file, sizeUnit = 'kb' }: ParseFileInfoOptions): FileInfo | undefined => {
    if (!file) {
        return undefined
    }
    const fileSize = file.size
    const fileSizeUnit = sizeUnit
    const fileType = file.type.split('/')[1]
    const fileName = file.name.replaceAll('"', '')

    if (fileSizeUnit === 'b') {
        return {
            name: fileName,
            size: {
                value: fileSize,
                unit: 'b'
            },
            type: file.type
        }
    }

    const unitIndex = fileSizeUnits.indexOf(fileSizeUnit)
    const size = parseFloat((fileSize / Math.pow(1024, unitIndex)).toFixed(2))

    return {
        name: fileName,
        size: {
            value: size,
            unit: fileSizeUnits[unitIndex]
        },
        type: fileType
    }
}

const logoMap: Record<string, StaticImageData> = {
    png: pngLogo,
    pdf: pdfLogo,
    jpeg: jpegLogo,
    jpg: jpgLogo,
    json: jsonLogo
}

export const onDownloadAttachment = (file: File) => {
    resolveFileOnBrowser(file, file.name, true)
}

export const onViewAttachment = (file: File) => {
    resolveFileOnBrowser(file, file.name, false)
}

export const fileTypeToLogo = (fileType: string | undefined) => {
    return logoMap[fileType ?? ''] || null
}

export type FetchUrlAsFileOptions = {
    url: string
    filename?: string
    mimeType?: string
    proxyUrl?: string
}

export const fetchUrlAsFile = async ({
    url,
    filename = 'download',
    mimeType = 'application/pdf',
    proxyUrl
}: FetchUrlAsFileOptions): Promise<File> => {
    const fetchUrl = proxyUrl ? `${proxyUrl}?url=${encodeURIComponent(url)}` : url
    const response = await fetch(fetchUrl)

    if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`)
    }

    const blob = await response.blob()

    if (blob.size === 0) {
        throw new Error('Received empty file')
    }

    return new File([blob], filename, { type: mimeType })
}

export const generateFakeDocument = async (format: 'pdf' | 'jpeg' | 'png', label: string): Promise<Blob> => {
    const canvas = document.createElement('canvas')

    if (format === 'pdf') {
        canvas.width = 595 // A4 width in pixels (72 DPI)
        canvas.height = 842 // A4 height in pixels (72 DPI)
    } else {
        canvas.width = 800
        canvas.height = 600
    }

    const ctx = canvas.getContext('2d')
    if (!ctx) {
        throw new Error('Failed to create canvas context')
    }

    ctx.fillStyle = '#f0f0f0'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#000'
    ctx.font = '20px Arial'
    ctx.fillText(label, 50, 100) // Use label like 'Front Side', 'Back Side', etc.

    return new Promise((resolve, reject) => {
        const mimeType = format === 'pdf' ? 'application/pdf' : `image/${format}`

        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob)
            } else {
                reject(new Error(`Failed to generate ${format} blob`))
            }
        }, mimeType)
    })
}
